import React, { useEffect, useState } from 'react';
import { Mail, Send, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../utils/constants';

const Support = ({ onNavigate, token }) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [error, setError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileReady, setTurnstileReady] = useState(false);
  const [turnstileError, setTurnstileError] = useState('');
  const isLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY
    || (isLocalhost ? '1x00000000000000000000AA' : '0x4AAAAAACNJYmSG3y0dFDYD');

  useEffect(() => {
    window.__onTurnstileSuccess = (tokenValue) => {
      setTurnstileToken(tokenValue);
    };

    if (!document.querySelector('script[data-turnstile-script]')) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.dataset.turnstileScript = 'true';
      script.onload = () => setTurnstileReady(true);
      script.onerror = () => {
        setTurnstileError(t('support.turnstileLoadError'));
        setTurnstileReady(false);
      };
      document.body.appendChild(script);
    } else {
      setTurnstileReady(true);
    }

    return () => {
      window.__onTurnstileSuccess = undefined;
    };
  }, [t]);

  useEffect(() => {
    if (!turnstileReady || !window.turnstile) {
      return;
    }

    const container = document.getElementById('support-turnstile');
    if (container && !container.dataset.rendered) {
      try {
        window.turnstile.render(container, {
          sitekey: siteKey,
          callback: (tokenValue) => setTurnstileToken(tokenValue),
        });
        container.dataset.rendered = 'true';
      } catch (err) {
        setTurnstileError(t('support.turnstileInitError'));
      }
    }
  }, [turnstileReady, siteKey, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      setError(t('support.messageRequired'));
      setStatus('error');
      return;
    }

    setStatus('sending');
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/support`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          subject: subject.trim(),
          message: message.trim(),
          turnstile_token: turnstileToken,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || t('support.sendFailed'));
      }

      setStatus('success');
      setName('');
      setEmail('');
      setSubject('');
      setMessage('');
      setTurnstileToken('');
      if (window.turnstile) {
        window.turnstile.reset();
      }
    } catch (err) {
      setStatus('error');
      setError(err.message || t('support.sendFailed'));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 max-w-xl w-full">
        <button
          onClick={() => onNavigate('/')}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('support.backToDashboard')}
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
            <Mail className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('support.title')}</h1>
            <p className="text-sm text-gray-600">{t('support.subtitle')}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="support-name" className="sr-only">{t('support.namePlaceholder')}</label>
              <input
                id="support-name"
                type="text"
                autoComplete="name"
                placeholder={t('support.namePlaceholder')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label htmlFor="support-email" className="sr-only">{t('support.emailPlaceholder')}</label>
              <input
                id="support-email"
                type="email"
                autoComplete="email"
                placeholder={t('support.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>
          <label htmlFor="support-subject" className="sr-only">{t('support.subjectPlaceholder')}</label>
          <input
            id="support-subject"
            type="text"
            placeholder={t('support.subjectPlaceholder')}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
          <label htmlFor="support-message" className="sr-only">{t('support.messagePlaceholder')}</label>
          <textarea
            id="support-message"
            rows="6"
            required
            placeholder={t('support.messagePlaceholder')}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
          />
          <div className="min-h-[72px]">
            <div id="support-turnstile" className="flex justify-center" />
          </div>
          {turnstileError && (
            <p className="text-xs text-amber-600">{turnstileError}</p>
          )}

          {status === 'success' && (
            <p className="text-sm text-green-600">{t('support.messageSent')}</p>
          )}
          {status === 'error' && (
            <p className="text-sm text-red-600">{error}</p>
          )}

          <button
            type="submit"
            disabled={status === 'sending' || !turnstileToken}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition font-medium disabled:opacity-60"
          >
            <Send className="w-4 h-4" />
            {status === 'sending' ? t('support.sending') : t('support.sendMessage')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Support;
